import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { TRACKS } from "../data/tracks";

const AudioCtx = createContext(null);

// Hard preview cap: no track (they're hotlinked full-length tracks from
// portalsofawe.com, several 5+ minutes) plays past this many seconds.
// Applies everywhere this context is used — homepage cards, Quiz band,
// Gift page, Graceful Passages, and the full /listen room.
const PREVIEW_CAP_SECONDS = 60;

export const useAudio = () => {
  const ctx = useContext(AudioCtx);
  if (!ctx) throw new Error("useAudio must be used within AudioProvider");
  return ctx;
};

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  if (!audioRef.current && typeof Audio !== "undefined") {
    audioRef.current = new Audio();
    audioRef.current.preload = "metadata";
  }

  const [currentId, setCurrentId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.85);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const current = TRACKS.find((t) => t.id === currentId) || null;

  const playTrack = useCallback((id) => {
    const track = TRACKS.find((t) => t.id === id);
    const audio = audioRef.current;
    if (!track || !audio) return;

    if (currentId === id) {
      if (audio.paused) audio.play().catch(() => {});
      else audio.pause();
      return;
    }
    setLoading(true);
    audio.src = track.url;
    audio.volume = volume;
    setCurrentId(id);
    audio.play().catch(() => setLoading(false));
  }, [currentId, volume]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !currentId) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  }, [currentId]);

  const step = useCallback((dir) => {
    const idx = TRACKS.findIndex((t) => t.id === currentId);
    const nextIdx = (idx + dir + TRACKS.length) % TRACKS.length;
    playTrack(TRACKS[nextIdx].id);
  }, [currentId, playTrack]);

  const seek = useCallback((ratio) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const cappedDuration = Math.min(audio.duration, PREVIEW_CAP_SECONDS);
    const target = Math.min(ratio * cappedDuration, PREVIEW_CAP_SECONDS);
    audio.currentTime = target;
    setProgress(target);
  }, []);

  const setVolume = useCallback((v) => {
    const audio = audioRef.current;
    setVolumeState(v);
    if (audio) audio.volume = v;
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    setCurrentId(null);
    setProgress(0);
    setDuration(0);
    setExpanded(false);
    setIsPlaying(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      if (audio.currentTime >= PREVIEW_CAP_SECONDS) {
        audio.pause();
        audio.currentTime = PREVIEW_CAP_SECONDS;
        setProgress(PREVIEW_CAP_SECONDS);
        return;
      }
      setProgress(audio.currentTime);
    };
    const onDur = () => setDuration(Math.min(audio.duration || 0, PREVIEW_CAP_SECONDS));
    const onPlay = () => { setIsPlaying(true); setLoading(false); };
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setLoading(true);
    const onPlaying = () => setLoading(false);
    const onEnded = () => step(1);
    const onError = () => { setLoading(false); setIsPlaying(false); };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onDur);
    audio.addEventListener("durationchange", onDur);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onDur);
      audio.removeEventListener("durationchange", onDur);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [step]);

  const value = {
    tracks: TRACKS,
    current, currentId, isPlaying, loading,
    progress, duration, volume, expanded,
    playTrack, toggle, step, seek, setVolume, stop,
    setExpanded,
  };

  return <AudioCtx.Provider value={value}>{children}</AudioCtx.Provider>;
};
